import { useState, useEffect } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Button } from "../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { ComputedTableColumnDataType } from "../../../models/workPaperTemplate";

export interface ComputedTableColumnFormData {
  id: string;
  name: string;
  dataType: ComputedTableColumnDataType;
  instructions: string | null;
  columnOrder: number;
}

export interface ComputedTableFieldFormData {
  sheetName: string;
  startRow: number;
  headerRow: number;
  columnStart: string;
  context: "test_results" | "control_documentation";
  instructions: string;
  columns: ComputedTableColumnFormData[];
}

interface ComputedTableFieldFormProps {
  value: ComputedTableFieldFormData;
  onChange: (value: ComputedTableFieldFormData) => void;
  readOnly?: boolean;
}

let columnIdCounter = 0;

function generateTempColumnId(): string {
  columnIdCounter += 1;
  return `temp-column-${columnIdCounter}`;
}

export function ComputedTableFieldForm({
  value,
  onChange,
  readOnly = false,
}: ComputedTableFieldFormProps) {
  const [formData, setFormData] = useState<ComputedTableFieldFormData>(value);

  useEffect(() => {
    setFormData(value);
  }, [value]);

  const handleChange = <K extends keyof ComputedTableFieldFormData>(
    field: K,
    newValue: ComputedTableFieldFormData[K],
  ) => {
    const updated = { ...formData, [field]: newValue };
    setFormData(updated);
    onChange(updated);
  };

  const handleColumnChange = (
    index: number,
    field: keyof ComputedTableColumnFormData,
    newValue: string | number | null,
  ) => {
    const newColumns = [...formData.columns];
    newColumns[index] = { ...newColumns[index], [field]: newValue };
    handleChange("columns", newColumns);
  };

  const addColumn = () => {
    const newColumn: ComputedTableColumnFormData = {
      id: generateTempColumnId(),
      name: "",
      dataType: "string",
      instructions: null,
      columnOrder: formData.columns.length,
    };
    handleChange("columns", [...formData.columns, newColumn]);
  };

  const removeColumn = (index: number) => {
    const newColumns = formData.columns
      .filter((_, i) => i !== index)
      .map((col, i) => ({ ...col, columnOrder: i }));
    handleChange("columns", newColumns);
  };

  const moveColumn = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= formData.columns.length) return;
    const newColumns = [...formData.columns];
    const [removed] = newColumns.splice(fromIndex, 1);
    newColumns.splice(toIndex, 0, removed);
    const reordered = newColumns.map((col, i) => ({ ...col, columnOrder: i }));
    handleChange("columns", reordered);
  };

  return (
    <div className="space-y-6">
      {/* Sheet Configuration */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700">
          Sheet Configuration
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="sheetName">Sheet Name</Label>
            <Input
              id="sheetName"
              value={formData.sheetName}
              onChange={(e) => handleChange("sheetName", e.target.value)}
              placeholder="Sheet1"
              disabled={readOnly}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="columnStart">Starting Column</Label>
            <Input
              id="columnStart"
              value={formData.columnStart}
              onChange={(e) =>
                handleChange("columnStart", e.target.value.toUpperCase())
              }
              placeholder="A"
              disabled={readOnly}
              className="uppercase"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="headerRow">Header Row</Label>
            <Input
              id="headerRow"
              type="number"
              min={1}
              value={formData.headerRow}
              onChange={(e) =>
                handleChange("headerRow", parseInt(e.target.value, 10) || 1)
              }
              placeholder="1"
              disabled={readOnly}
            />
            <p className="text-xs text-muted-foreground">
              Row for column headers
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="startRow">Data Start Row</Label>
            <Input
              id="startRow"
              type="number"
              min={1}
              value={formData.startRow}
              onChange={(e) =>
                handleChange("startRow", parseInt(e.target.value, 10) || 2)
              }
              placeholder="2"
              disabled={readOnly}
            />
            <p className="text-xs text-muted-foreground">
              Row where data begins
            </p>
          </div>
        </div>
      </div>

      {/* Context and Instructions */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700">
          AI Generation Settings
        </h4>

        <div className="space-y-2">
          <Label htmlFor="context">Context Source</Label>
          <Select
            value={formData.context}
            onValueChange={(value) =>
              handleChange(
                "context",
                value as "test_results" | "control_documentation",
              )
            }
            disabled={readOnly}
          >
            <SelectTrigger id="context">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="test_results">Test Results</SelectItem>
              <SelectItem value="control_documentation">
                Control Documentation
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            The source of information the AI will use to generate table data
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="instructions">Table Generation Instructions</Label>
          <Textarea
            id="instructions"
            value={formData.instructions}
            onChange={(e) => handleChange("instructions", e.target.value)}
            placeholder="Example: Generate a table summarizing each sample tested, including any exceptions or issues found"
            rows={4}
            disabled={readOnly}
          />
          <p className="text-xs text-muted-foreground">
            Overall instructions for how the AI should populate this table
          </p>
        </div>
      </div>

      {/* Column Definitions */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700">Table Columns</h4>
          {!readOnly && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addColumn}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Column
            </Button>
          )}
        </div>

        {formData.columns.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-4 border rounded-md border-dashed">
            No columns defined. Add at least one column.
          </div>
        ) : (
          <div className="space-y-3">
            {formData.columns.map((column, index) => (
              <div
                key={column.id}
                className="border rounded-md p-3 space-y-3 bg-gray-50"
              >
                <div className="flex items-center gap-2">
                  {!readOnly && (
                    <div className="flex flex-col gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0"
                        onClick={() => moveColumn(index, index - 1)}
                        disabled={index === 0}
                      >
                        <GripVertical className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                  <span className="text-xs text-muted-foreground font-medium">
                    Column {index + 1}
                  </span>
                  {!readOnly && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 ml-auto text-destructive hover:text-destructive"
                      onClick={() => removeColumn(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Column Name</Label>
                    <Input
                      value={column.name}
                      onChange={(e) =>
                        handleColumnChange(index, "name", e.target.value)
                      }
                      placeholder="e.g., Sample ID, Finding"
                      disabled={readOnly}
                      className="h-8 text-sm"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Data Type</Label>
                    <Select
                      value={column.dataType}
                      onValueChange={(value) =>
                        handleColumnChange(
                          index,
                          "dataType",
                          value as ComputedTableColumnDataType,
                        )
                      }
                      disabled={readOnly}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="string">Text</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                        <SelectItem value="boolean">Yes/No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">
                    Column Instructions (Optional)
                  </Label>
                  <Textarea
                    value={column.instructions ?? ""}
                    onChange={(e) =>
                      handleColumnChange(
                        index,
                        "instructions",
                        e.target.value || null,
                      )
                    }
                    placeholder="Specific instructions for this column..."
                    rows={2}
                    disabled={readOnly}
                    className="text-sm"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview */}
      <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm">
        <strong>Preview:</strong> {formData.sheetName} - Table starting at{" "}
        {formData.columnStart}
        {formData.headerRow} with {formData.columns.length} column
        {formData.columns.length !== 1 ? "s" : ""}
        {formData.columns.length > 0 && (
          <span className="text-muted-foreground">
            {" "}
            ({formData.columns.map((c) => c.name || "Unnamed").join(", ")})
          </span>
        )}
      </div>
    </div>
  );
}
